import { fetchFromPublicApiUsingPartialPath } from 'Utilities'

import {
  checkReturnLabelTokenResponseSchema,
  type SubmitReturnLabelResponse,
  submitReturnLabelResponseSchema,
} from './PublicRequestReturnLabel.schema'
import type {
  CheckReturnLabelTokenResult,
  SubmitReturnLabelPayload,
} from './PublicRequestReturnLabel.types'

export type { SubmitReturnLabelResponse }

const CHECK_TOKEN_PATH = '/return-label/check-token'
const JSON_CONTENT_TYPE = 'application/json'

export const submitPublicReturnLabel = async (
  payload: SubmitReturnLabelPayload,
): Promise<SubmitReturnLabelResponse> => {
  const raw = await fetchFromPublicApiUsingPartialPath<unknown>(
    '/return-label/submit',
    {
      body: JSON.stringify(payload),
      headers: { 'Content-Type': JSON_CONTENT_TYPE },
      method: 'POST',
    },
  )
  const result = submitReturnLabelResponseSchema.safeParse(raw)

  if (!result.success) {
    throw new Error('Invalid submit response')
  }

  return result.data
}

export const checkPublicReturnLabelToken = async (
  token: string,
): Promise<CheckReturnLabelTokenResult> => {
  try {
    const response = await fetch(`${window.GO_API_URL}${CHECK_TOKEN_PATH}`, {
      body: JSON.stringify({ token }),
      headers: { 'Content-Type': JSON_CONTENT_TYPE },
      method: 'POST',
    })

    if (!response.ok) {
      return { kind: 'invalid' }
    }

    const raw: unknown = await response.json().catch(() => null)
    const parsed = checkReturnLabelTokenResponseSchema.safeParse(raw)

    if (!parsed.success) {
      return { kind: 'invalid' }
    }

    if (parsed.data.regenerated) {
      return { kind: 'regenerated', newToken: parsed.data.token }
    }

    return parsed.data.prefilled
      ? { kind: 'valid_prefilled' }
      : { kind: 'valid_no_identifier' }
  } catch {
    return { kind: 'invalid' }
  }
}

/**
 * SM-2125 contract: GET /return-label/download/:tracking_reference?token=<TOKEN>.
 * Returns the standard return-label PDF. When the submit response carries
 * hasDangerousGoods=true a second PDF is exposed via buildDangerousGoodsDownloadUrl.
 * No fetch is issued from the FE - the URL is embedded in an anchor href and
 * the browser handles the download natively via Content-Disposition: attachment.
 */
export const buildDownloadUrl = (
  trackingReference: string,
  requestToken: string,
): string =>
  `${window.GO_API_URL}/return-label/download/${encodeURIComponent(
    trackingReference,
  )}?token=${encodeURIComponent(requestToken)}`

/**
 * SM-2211 contract: GET /return-label/dangerous-goods/:tracking_reference?token=<TOKEN>.
 * Same anchor-href / Content-Disposition pattern as buildDownloadUrl; rendered
 * only when the submit response carries hasDangerousGoods=true.
 */
export const buildDangerousGoodsDownloadUrl = (
  trackingReference: string,
  requestToken: string,
): string =>
  `${window.GO_API_URL}/return-label/dangerous-goods/${encodeURIComponent(
    trackingReference,
  )}?token=${encodeURIComponent(requestToken)}`
